import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';

import { AdminLayoutComponent } from './layouts/admin-layout/admin-layout.component';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout.component';

import { AuthGuard } from '../app/_helpers/auth.guard';
import { environment } from 'src/environments/environment';
import { GatewayResolver } from './_services/gateway.resolver';
import { AgentsComponent } from './pages/agents/agents.component';
const name = environment.envVar.NAME;

const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'spaces' },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard/dashboard.component').then(
            m => m.DashboardComponent
          ),
        canActivate: [AuthGuard],
        title: `Dashboard | ${name}`,
      },
      {
        path: 'asset-tree',
        loadComponent: () =>
          import('./pages/asset-tree/asset-tree.component').then(
            m => m.AssetTreeComponent
          ),
        canActivate: [AuthGuard],
        title: `Asset Relationship Tree | ${name}`,
      },
      {
        path: 'buildings/:buildingId',
        loadComponent: () =>
          import('./pages/building-report/building-report.component').then(
            m => m.BuildingReportComponent
          ),
        canActivate: [AuthGuard],
        title: `Building Details | ${name}`,
      },
      {
        path: 'spaces',
        loadComponent: () =>
          import('./pages/spaces/spaces.component').then(
            m => m.SpacesComponent
          ),
        canActivate: [AuthGuard],
        title: `Spaces | ${name}`,
      },
      {
        path: 'assets',
        loadComponent: () =>
          import('./pages/assets/assets.component').then(
            m => m.AssetsComponent
          ),
        canActivate: [AuthGuard],
        title: `Assets | ${name}`,
      },
      {
        path: 'assets/:assetId',
        loadComponent: () =>
          import('./pages/asset-report/asset-report.component'),
        canActivate: [AuthGuard],
        title: `Asset Details | ${name}`,
      },
      {
        path: 'asset-models',
        loadComponent: () =>
          import('./pages/asset-models/asset-models.component').then(
            m => m.AssetModelsComponent
          ),
        canActivate: [AuthGuard],
        title: `Asset Models | ${name}`,
      },
      {
        path: 'device-library',
        loadComponent: () =>
          import('./pages/device-models/device-models.component').then(
            m => m.DeviceModelsComponent
          ),
        canActivate: [AuthGuard],
        title: `Device Library | ${name}`,
      },
      {
        path: 'device-library/:deviceModelId',
        loadComponent: () =>
          import(
            './pages/device-model-detail/device-model-detail.component'
          ).then(m => m.DeviceModelDetailComponent),
        canActivate: [AuthGuard],
        title: `Device Library Details | ${name}`,
      },
      {
        path: 'devices',
        loadComponent: () =>
          import('./pages/devices/devices.component').then(
            m => m.DevicesComponent
          ),
        canActivate: [AuthGuard],
        title: `Devices | ${name}`,
      },
      {
        path: 'devices/:deviceId',
        loadComponent: () =>
          import('./pages/device-detail/device-detail.component').then(
            m => m.DeviceDetailComponent
          ),
        canActivate: [AuthGuard],
        title: `Device Details | ${name}`,
      },
      {
        path: 'automations',
        loadComponent: () =>
          import('./pages/rules/rules.component').then(m => m.RulesComponent),
        canActivate: [AuthGuard],
        title: `Rules | ${name}`,
      },
      {
        path: 'report-generator',
        loadComponent: () =>
          import('./pages/rule-report/rule-report.component').then(
            m => m.RuleReportComponent
          ),
        canActivate: [AuthGuard],
        title: `Report Generator | ${name}`,
      },
      {
        path: 'rules',
        loadComponent: () =>
          import('./pages/rules/rules.component').then(m => m.RulesComponent),
        canActivate: [AuthGuard],
        title: `Rules | ${name}`,
      },
      {
        path: 'rules/:ruleId',
        loadComponent: () =>
          import('./pages/rule-detail/rule-detail.component').then(
            m => m.RuleDetailComponent
          ),
        canActivate: [AuthGuard],
        title: `Rule Detail | ${name}`,
      },
      {
        path: 'schedules/:scheduleId',
        loadComponent: () =>
          import('./pages/schedule-detail/schedule-detail.component').then(
            m => m.ScheduleDetailComponent
          ),
        canActivate: [AuthGuard],
        title: `Schedule Detail | ${name}`,
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./pages/users/users.component').then(m => m.UsersComponent),
        canActivate: [AuthGuard],
        title: `Users | ${name}`,
      },
      {
        path: 'users/:userId',
        loadComponent: () =>
          import('./pages/user-detail/user-detail.component').then(
            m => m.UserDetailComponent
          ),
        canActivate: [AuthGuard],
        title: `User Details | ${name}`,
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings/settings.component').then(
            m => m.SettingsComponent
          ),
        canActivate: [AuthGuard],
        title: `Settings | ${name}`,
      },
      {
        path: 'not-allowed',
        loadComponent: () =>
          import('./pages/no-permission/no-permission.component').then(
            m => m.PermissionComponent
          ),
        title: `Not Allowed | ${name}`,
      },
      {
        path: 'gateways',
        loadChildren: () =>
          import('@semvar/gateways').then(m => m.GatewaysModule),
        canActivate: [AuthGuard],
        title: `Gateways | ${name}`,
        resolve: {
          data: GatewayResolver,
        },
      },
      {
        path: 'features',
        loadComponent: () =>
          import('./pages/features/features.component').then(
            m => m.FeaturesComponent
          ),
        canActivate: [AuthGuard],
        title: `Features | ${name}`,
      },
      {
        path: 'reports/builder',
        loadComponent: () =>
          import('./pages/report-builder/report-builder.page.component').then(
            m => m.ReportBuilderPageComponent
          ),
        canActivate: [AuthGuard],
        title: `Report Builder | ${name}`,
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('./pages/reports/reports.component').then(
            m => m.ReportsComponent
          ),
        canActivate: [AuthGuard],
        title: `Reports | ${name}`,
      },
      {
        path: 'reports/:reportId',
        loadComponent: () =>
          import('./pages/report-detail/report-detail.component').then(
            m => m.ReportDetailComponent
          ),
        canActivate: [AuthGuard],
        title: `Report Detail | ${name}`,
      },
      {
        path: 'events',
        loadComponent: () =>
          import('./pages/events/events.component').then(
            m => m.EventsComponent
          ),
        canActivate: [AuthGuard],
        title: `Events | ${name}`,
      },
      {
        path: 'agents',
        component: AgentsComponent,
        title: `AI Agents | ${name}`,
      },
      {
        path: 'dashboards',
        loadComponent: () =>
          import('./pages/dashboard-builder/dashboard-builder.component').then(
            m => m.DashboardBuilderComponent
          ),
        canActivate: [AuthGuard],
        title: `Dashboard Builder | ${name}`,
      },
    ],
  },
  {
    path: 'auth',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./layouts/auth-layout/auth-layout.module').then(
            m => m.AuthLayoutModule
          ),
      },
    ],
  },
  {
    path: 'public/:tenantId/assets/:assetId',
    loadComponent: () =>
      import('./pages/public-asset-report/public-asset-report.component'),
    title: `${name}`,
  },
  {
    path: 'public/:tenantId/dashboards',
    loadComponent: () =>
      import('./pages/public-dashboard/public-dashboard.component'),
    title: `Dashboard | ${name}`,
  },
  {
    path: '**',
    redirectTo: 'spaces',
  },
];

@NgModule({
  imports: [
    CommonModule,
    BrowserModule,
    RouterModule.forRoot(routes, {
      useHash: true,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
